import React, { useMemo } from 'react'
import { Box } from '@material-ui/core'
import { Chip, CircularProgress, Typography } from '@material-ui/core'
import { Link } from 'react-router-dom'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import FavoriteIcon from '@material-ui/icons/Favorite'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import ImageNA from '../../images/ImageNA.svg'
import { formatDate, normalize, parseDateOnly } from 'util/CommonUtils'

const ProjectMetricsView = ({
  projectDetailsLoading,
  projects,
  projectDetailsMap = {},
  projectStatusById = {},
  classes,
  onMetricsScroll,
  metricsContainerRef,
}) => {
  const today = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }, [])

  const getProjectStatus = (project) => {
    const projectId = normalize(project.id || project.project_id)
    const statusData = projectStatusById[projectId]

    // Use total_completion (count) and total_tcins from the fetch_project_status API response
    const done = statusData?.total_completion ?? 0
    const total = statusData?.total_tcins ?? 0
    const percent = total > 0 ? Math.round((done / total) * 100) : 0

    // Use project_start_date from API if available, fall back to project.createDate
    const startDate =
      parseDateOnly(statusData?.project_start_date) ||
      parseDateOnly(project.createDate)
    const endDate = parseDateOnly(project.samplesRequiredBy)

    const isCompleted = total > 0 && done >= total
    const isDue =
      !isCompleted && !!endDate && today.getTime() > endDate.getTime()
    const isInProgress =
      !isCompleted &&
      !isDue &&
      !!startDate &&
      today.getTime() >= startDate.getTime() &&
      (!endDate || today.getTime() <= endDate.getTime())

    let statusLabel = 'In Progress'
    let statusColor = '#1976d2'
    if (isCompleted) {
      statusLabel = 'Completed'
      statusColor = '#2e7d32'
    } else if (isDue) {
      statusLabel = 'Overdue'
      statusColor = '#d32f2f'
    }

    return {
      done,
      total,
      percent,
      isCompleted,
      isDue,
      isInProgress,
      statusLabel,
      statusColor,
      startDate,
      endDate,
    }
  }
  const getProgressBarColor = (percent, isDue) => {
    if (isDue) {
      return 'linear-gradient(90deg, #CC0000 0%, #e84040 50%, #f08080 100%)'
    }
    if (percent >= 75) {
      return 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)'
    }
    return 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #fde68a 100%)'
  }

  const getBadgeColor = (percent, isDue) => {
    if (isDue) {
      return 'linear-gradient(135deg, #CC0000 0%, #e84040 100%)'
    }
    if (percent >= 75) {
      return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
    }
    return 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
  }

  const getDaysLeftLabel = (endDate) => {
    if (!endDate) return 'No due date'
    const diffMs = endDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000))
    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Due today'
    return `${diffDays} days left`
  }
  return (
    <Box
      ref={metricsContainerRef}
      onScroll={onMetricsScroll}
      style={{
        padding: '16px 24px',
        maxHeight: 'calc(100vh - 260px)',
        overflowY: 'auto',
      }}
    >
      {(projects || [])?.map((project) => {
        const projectDetail =
          projectDetailsMap[normalize(project.id || project.project_id)] || {}
        const tcinMainImages = (projectDetail.tcin_info || [])
          ?.map((tcinInfo) => {
            const url =
              tcinInfo?.image_information?.main?.source_image
                ?.preview_image_url || tcinInfo?.item_info?.images?.main
            return url
              ? {
                  url,
                  tcin: tcinInfo?.tcin || 'TCIN',
                }
              : null
          })
          .filter((img) => img)
          .slice(0, 10)
        const {
          done,
          total,
          percent,
          isDue,
          statusLabel,
          statusColor,
          startDate,
          endDate,
        } = getProjectStatus(project)
        const daysLeftLabel = getDaysLeftLabel(endDate)

        return (
          <Box
            key={project.id || project.project_id}
            style={{
              padding: '16px 24px',
              marginBottom: 0,
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#fff',
            }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              {/* <Box> */}
              <Box style={{ marginBottom: 8 }}>
                <Box
                  style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}
                >
                  {project.isFavourite ? (
                    <FavoriteIcon
                      style={{ color: '#cc0000', fontSize: 16 }}
                      aria-label="Favorite project"
                    />
                  ) : (
                    <FavoriteBorderIcon
                      style={{ color: '#8a8a8a', fontSize: 16 }}
                      aria-label="Not favorite project"
                    />
                  )}
                  <Link
                    to={
                      '/projects/' +
                      (project.id || project.project_id) +
                      '/view'
                    }
                    onClick={() =>
                      localStorage.setItem('projecttab', 'metrics')
                    }
                    style={{
                      fontWeight: 600,
                      fontSize: 16,
                      color: '#333',
                      textDecoration: 'underline',
                    }}
                  >
                    {project.name || project.project_name}
                  </Link>
                  <Chip
                    label={statusLabel}
                    size="small"
                    style={{
                      backgroundColor: statusColor,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                </Box>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}
                  >
                    Owner:{' '}
                    <span style={{ color: '#111827', fontWeight: 700 }}>
                      {project.createdBy || project.created_by || '--'}
                    </span>
                  </Typography>
                </Box>
              </Box>

              {/* </Box> */}
              <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CalendarTodayIcon style={{ color: '#666', fontSize: 16 }} />
                  <Typography
                    variant="body2"
                    style={{ color: '#666', fontSize: 13 }}
                  >
                    {formatDate(startDate)}
                    {' \u2192 '}
                    {endDate ? formatDate(endDate) : '\u2014'}
                  </Typography>
                </Box>
                <Box
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 999,
                    backgroundColor: '#eaf2ff',
                    color: '#1f6feb',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <AccessTimeIcon style={{ fontSize: 16 }} />
                  <span>{daysLeftLabel}</span>
                </Box>
              </Box>
            </Box>

            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <Box
                style={{
                  minWidth: 75,
                  height: 75,
                  borderRadius: '50%',
                  background: getBadgeColor(percent, isDue),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: isDue
                    ? '0 2px 10px rgba(204,0,0,0.4)'
                    : percent >= 75
                      ? '0 2px 10px rgba(16,185,129,0.45)'
                      : '0 2px 10px rgba(245,158,11,0.4)',
                }}
              >
                <Typography
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: '#fff',
                    lineHeight: 1,
                  }}
                >
                  {percent || 0}%
                </Typography>
              </Box>

              <Box style={{ flex: 1 }}>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'rgba(35,41,60,0.65)',
                    }}
                  >
                    TCIN Completion :{' '}
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: 'rgba(35,41,60,0.85)',
                      }}
                    >
                      {`${done} / ${total} (${percent}%)`}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'rgba(35,41,60,0.65)',
                        marginLeft: 12,
                      }}
                    >
                      <span style={{ marginRight: 12 }}>|</span>
                      {'Samples Received: '}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: 'rgba(35,41,60,0.85)',
                        }}
                      >
                        {`${project.percentageSampleReceived || 0}%`}
                      </span>
                    </span>
                  </Typography>
                </Box>
                <Box
                  style={{
                    width: '100%',
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: 'rgba(35,41,60,0.07)',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    style={{
                      width: `${percent}%`,
                      height: '100%',
                      borderRadius: 6,
                      background: getProgressBarColor(percent, isDue),
                      transition: 'width 0.7s cubic-bezier(.4,0,.2,1)',
                    }}
                  />
                </Box>
                {/* {tcinMainImages.length > 0 && (
                  <Box
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      margin: 8,
                    }}
                  >
                    {tcinMainImages.map((img, idx) => {
                      if (img?.url?.includes('null')) {
                        return (
                          <ImageNA
                            key={`${project.id || project.project_id}-na-${idx}`}
                            alt="No Image"
                            title="No Image"
                            className={classes.noImage}
                            height={36}
                            width={36}
                          />
                        )
                      }
                      return (
                        <Box
                          key={`${project.id || project.project_id}-main-${idx}`}
                          component="img"
                          src={img.url}
                          alt={`${project.name || project.project_name} ${img.tcin}`}
                          title={img.tcin}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 4,
                            objectFit: 'cover',
                            border: '1px solid rgba(0,0,0,0.08)',
                          }}
                        />
                      )
                    })}
                  </Box>
                )} */}
              </Box>
            </Box>
          </Box>
        )
      })}
      {projectDetailsLoading && (
        <Box
          sx={{
            minHeight: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 0',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
    </Box>
  )
}

export default React.memo(ProjectMetricsView)
